import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type MultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';

const isLegacySkillFilter = (filter: Filter) => {
  return (
    filter.id === FilterId.SKILL &&
    filter.type === FilterType.TREE &&
    filter.selections
  );
};

export const validateSkillTaxonomyFilterValue = (
  value: unknown
): value is CustomSkillTaxonomySelection => {
  if (typeof value !== 'object' || value === null) return false;

  if ('taxonomyId' in value && typeof value.taxonomyId === 'number') {
    return true;
  }

  return false;
};

export const validateSkillFilters = ({
  filters,
  taxonomyVersion,
  onRemoveFilter,
}: {
  filters: [FilterId, Filter][];
  taxonomyVersion?: string;
  onRemoveFilter?: (filterId: FilterId) => void;
}): [FilterId, Filter][] => {
  const parsedFiltersArray = filters;
  let hasRemovedSkillFilter = false;

  const legacySkillFilter = parsedFiltersArray.find((item) =>
    isLegacySkillFilter(item[1])
  );

  const hasCustomSkill = parsedFiltersArray.some(
    ([id]) => id === FilterId.CUSTOM_SKILL
  );

  // get skill taxonomy filter
  const skillTaxonomyFilter = parsedFiltersArray.find(
    ([id]) => id === FilterId.SKILL_TAXONOMY
  )?.[1] as SingleValueFilter<unknown>;

  let updatedFilterState: [FilterId, Filter][] = parsedFiltersArray.map(
    ([id, filter]) => {
      if (legacySkillFilter && id === FilterId.SKILL) {
        const clearedSkill = {
          ...filter,
          primarySelections: [],
          selections: [],
        };
        hasRemovedSkillFilter = true;
        return [id, clearedSkill];
      }

      if (
        id === FilterId.MULTI_CUSTOM_SKILL &&
        filter.type === FilterType.MULTI
      ) {
        const multiFilter = filter as MultiFilter<TreeFilter>;
        const hasLegacyInstances = multiFilter.instances.some(
          (instance) => instance.id === FilterId.SKILL
        );

        if (hasLegacyInstances) {
          hasRemovedSkillFilter = true;
          return [id, { ...filter, instances: [] }];
        }
      }

      return [id, filter];
    }
  );

  if (skillTaxonomyFilter) {
    const value = skillTaxonomyFilter.value;
    let shouldMarkUserSubmitted = false;

    try {
      // if skill taxonomy does not have a value defined
      if (!value) throw new Error('Skill taxonomy value not defined');

      // if skill taxonomy value is invalid
      if (!validateSkillTaxonomyFilterValue(value)) {
        throw new Error('Invalid skill taxonomy filter value');
      }

      // if skill taxonomy version does not match the version in the URL
      if (taxonomyVersion && value.version !== taxonomyVersion) {
        throw new Error('Skill taxonomy version mismatch');
      }

      const typedValue = value as CustomSkillTaxonomySelection;
      const taxonomyId = typedValue?.taxonomyId;
      if (typeof taxonomyId === 'number' && taxonomyId !== 0) {
        shouldMarkUserSubmitted = true;
      }
      // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    } catch (error) {
      console.error(value);

      const customSkillFilter = updatedFilterState.find(
        ([id]) => id === FilterId.CUSTOM_SKILL
      )?.[1] as TreeFilter | undefined;

      if (customSkillFilter && customSkillFilter.selections?.length) {
        updatedFilterState = updatedFilterState.map(([id, filter]) =>
          id === FilterId.CUSTOM_SKILL
            ? [id, { ...filter, primarySelections: [], selections: [] }]
            : [id, filter]
        );

        hasRemovedSkillFilter = true;
      }

      const multiCustomSkillFilter = updatedFilterState.find(
        ([id]) => id === FilterId.MULTI_CUSTOM_SKILL
      )?.[1] as MultiFilter<TreeFilter> | undefined;

      if (multiCustomSkillFilter && multiCustomSkillFilter.instances?.length) {
        updatedFilterState = updatedFilterState.map(([id, filter]) =>
          id === FilterId.MULTI_CUSTOM_SKILL
            ? [id, { ...filter, instances: [] }]
            : [id, filter]
        );

        hasRemovedSkillFilter = true;
      }
    }

    if (shouldMarkUserSubmitted) {
      updatedFilterState = updatedFilterState.map(([id, filter]) =>
        id === FilterId.SKILL_TAXONOMY
          ? [
              id,
              {
                ...filter,
                isUserSubmitted: true,
              },
            ]
          : [id, filter]
      );
    }
  }

  if (!hasCustomSkill) {
    const def = getFilterDefinition(FilterId.CUSTOM_SKILL);
    if (def) {
      updatedFilterState.push([
        FilterId.CUSTOM_SKILL,
        { ...def, selections: [], primarySelections: [] },
      ]);
    }
  }

  if (hasRemovedSkillFilter) {
    onRemoveFilter?.(FilterId.SKILL);
  }
  return updatedFilterState;
};
